import { Constructor } from '../../../types/common/types';
import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { GridColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-column';
import { ButtonColor, FontSize } from '../../../ui/gzIconButton/types';
import { GridSelectionColumnElement } from '@vaadin/vaadin-grid/vaadin-grid-selection-column';
import { elementClassListAdd, elementClassListRemove, insertAfter } from '../../../utils/htmlElement.utils';
import { html, render } from 'lit';

export declare class HasRowDetailsInterface {
  buildRowDetailsToggle(hasRowDetails: boolean | undefined): undefined;
  collapseOpenRowDetails(): void;
}

export const HasRowDetailsMixin = <T extends Constructor<BaseWebComponentView>>(superClass: T) => {
  class GridHasRowDetails extends superClass {
    private readonly DETAILS_TOGGLE_COLUMN_WIDTH = '32px';

    private rowDetailsToggleRenderer(root, rowData) {
      if (!root.firstElementChild) {
        render(
          html`
            <gz-icon-button
              @click="${() => this.toggleRowDetailsDisplay(root)}"
              buttonColor="${ButtonColor.SECONDARY}"
              fontSize="${FontSize.ICON_SIZE_24}"
            ></gz-icon-button>
          `,
          root
        );
      }

      // We can check the `hasChildren` prop on the row data item model and display the button if true.
      // In this case, the `hasChildren` property needs to be implemented in every DataGridItem interface that
      // should display the row details
      const currentRow = rowData.item;
      !currentRow.hasChildren
        ? elementClassListAdd(root.firstElementChild, 'hidden')
        : elementClassListRemove(root.firstElementChild, 'hidden');
      root.item = currentRow;
      root.firstElementChild.icon = this.dom.$grid.detailsOpenedItems.includes(root.item)
        ? 'ds-icon-arrow_up_collapse'
        : 'ds-icon-arrow_down_expand';
    }

    private toggleRowDetailsDisplay(root) {
      const isOpened = this.dom.$grid.detailsOpenedItems.includes(root.item);
      if (isOpened) {
        this.dom.$grid.detailsOpenedItems = [];
        root.firstElementChild.icon = 'ds-icon-arrow_down_expand';
        this.dom.$grid.notifyResize();
      } else {
        this.dom.$grid.detailsOpenedItems = [root.item];
        root.firstElementChild.icon = 'ds-icon-arrow_up_collapse';
      }
    }

    public buildRowDetailsToggle(hasRowDetails = false) {
      if (!hasRowDetails) {
        return;
      }
      const detailsToggleColumn = document.createElement('vaadin-grid-column') as GridColumnElement;

      Object.assign(detailsToggleColumn, {
        flexGrow: 0,
        frozen: true,
        width: this.DETAILS_TOGGLE_COLUMN_WIDTH,
        resizable: false,
        headerRenderer: root => {
          root.textContent = '';
        },
        renderer: (root, _, rowData) => {
          root.classList.add('details-toggle__cell-content');
          return this.rowDetailsToggleRenderer(root, rowData);
        },
      });

      // should insert the toggle column after the checkboxes column
      if (this.dom.$grid.children[0] instanceof GridSelectionColumnElement) {
        insertAfter(detailsToggleColumn, this.dom.$grid.firstElementChild);
      } else {
        this.dom.$grid.prepend(detailsToggleColumn);
      }
    }

    public collapseOpenRowDetails() {
      this.dom.$grid.detailsOpenedItems = [];
      this.dom.$grid.render();
    }
  }

  return GridHasRowDetails as Constructor<HasRowDetailsInterface> & T;
};
